import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapIcon, List, RefreshCw } from 'lucide-react';
import { useTheme } from '../components/ui/theme-provider';
import { cn } from '../lib/utils';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Import new components
import { useJobData } from '../hooks/useJobData';
import JobFilters from '../components/jobs/JobFilters';
import JobListView from '../components/jobs/JobListView';
import JobDetailsModal from '../components/jobs/JobDetailsModal';
import JobMapView from '../components/jobs/JobMapView'; // Import the new map component
import { getJobTypeColor } from '../utils/jobUtils'; // Add import for legend
import HelpIcon from '../components/ui/HelpIcon'; // Import HelpIcon
import { useUserLocation } from '../hooks/useUserLocation';
import LocationPrompt from '../components/LocationPrompt';
import LocationDisplay from '../components/LocationDisplay'; // NEW IMPORT

// FIXED: Add MapLegend component with responsive design to prevent overflow
const MapLegend = ({ theme, distanceFilter }) => (
  <div className={cn(
    "mb-4 p-3 sm:p-4 rounded-lg border",
    theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
  )}>
    <div className="space-y-3">
      {/* Employment Types - FIXED: Responsive grid layout */}
      <div>
        <span className={cn(
          "text-sm font-medium mb-2 block",
          theme === 'light' ? 'text-gray-700' : 'text-gray-300'
        )}>
          Employment Types:
        </span>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap gap-2 lg:gap-3">
          {['Full-time', 'Part-time', 'NHS', 'Contract', 'Locum', 'Permanent', 'Temporary'].map(type => (
            <div key={type} className="flex items-center gap-1.5 min-w-0">
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: getJobTypeColor(type) }}
              ></div>
              <span className={cn(
                "text-xs truncate",
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                {type}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sources - FIXED: Removed redundant distance info */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className={cn(
          "text-sm font-medium",
          theme === 'light' ? 'text-gray-700' : 'text-gray-300'
        )}>
          Sources:
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ 
                backgroundColor: '#3B82F6',
                border: '2px solid #ffffff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            ></div>
            <span className={cn(
              "text-xs",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              BDJ Jobs
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ 
                backgroundColor: '#3B82F6',
                border: '2px solid #000000'
              }}
            ></div>
            <span className={cn(
              "text-xs",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              Practice Posted
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ENHANCED: Add CollapsibleMapLegend component with mobile-optimized behavior
const CollapsibleMapLegend = ({ theme, distanceFilter, isMobile = false }) => {
  const [isExpanded, setIsExpanded] = useState(!isMobile); // Auto-collapse on mobile

  // Auto-collapse when switching to mobile map view
  useEffect(() => {
    if (isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  if (isMobile) {
    return (
      <div className="mb-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full h-8 justify-between text-xs",
            theme === 'light' 
              ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50' 
              : 'bg-[#2A2A3E] border-gray-600 text-gray-300 hover:bg-gray-700'
          )}
        >
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
            </div>
            <span>Map Legend</span>
          </div>
          <span className={cn(
            "transform transition-transform duration-200",
            isExpanded ? "rotate-180" : "rotate-0"
          )}>
            ▼
          </span>
        </Button>
        
        {isExpanded && (
          <div className={cn(
            "mt-2 p-3 rounded-lg border animate-in slide-in-from-top-2 duration-200",
            theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
          )}>
            <MapLegendContent theme={theme} />
          </div>
        )}
      </div>
    );
  }

  // Desktop version - always expanded
  return (
    <div className={cn(
      "mb-4 p-3 sm:p-4 rounded-lg border",
      theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
    )}>
      <MapLegendContent theme={theme} />
    </div>
  );
};

// Extract legend content to reusable component
const MapLegendContent = ({ theme }) => (
  <div className="space-y-3">
    {/* Employment Types - Responsive grid layout */}
    <div>
      <span className={cn(
        "text-sm font-medium mb-2 block",
        theme === 'light' ? 'text-gray-700' : 'text-gray-300'
      )}>
        Employment Types:
      </span>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap gap-2 lg:gap-3">
        {['Full-time', 'Part-time', 'NHS', 'Contract', 'Locum', 'Permanent', 'Temporary'].map(type => (
          <div key={type} className="flex items-center gap-1.5 min-w-0">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: getJobTypeColor(type) }}
            ></div>
            <span className={cn(
              "text-xs truncate",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              {type}
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Sources */}
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <span className={cn(
        "text-sm font-medium",
        theme === 'light' ? 'text-gray-700' : 'text-gray-300'
      )}>
        Sources:
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ 
              backgroundColor: '#3B82F6',
              border: '2px solid #ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}
          ></div>
          <span className={cn(
            "text-xs",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            BDJ Jobs
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ 
              backgroundColor: '#3B82F6',
              border: '2px solid #000000'
            }}
          ></div>
          <span className={cn(
            "text-xs",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            Practice Posted
          </span>
        </div>
      </div>
    </div>
  </div>
);

const JobBrowsingPage = ({ user, currentUser }) => {
  // View state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [showFilters, setShowFilters] = useState(false);
  
  // Job details modal state
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  
  // User preferences state
  const [savedJobs, setSavedJobs] = useState([]);
  
  // Add distance filter state to track current filter
  const [currentDistanceFilter, setCurrentDistanceFilter] = useState(0);
  
  // FIXED: Move hasUserChangedRole state declaration to the top, before any useEffect that uses it
  const [hasUserChangedRole, setHasUserChangedRole] = useState(false); // Track if user manually changed role
  
  // NEW: Add mobile detection state
  const [isMobile, setIsMobile] = useState(false);
  
  const { theme } = useTheme();
  
  // Get user location for distance calculations - add basic error logging
  const {
    location: userLocation,
    loading: locationLoading,
    error: locationError,
    showLocationPrompt,
    setShowLocationPrompt,
    handleManualLocationEntry,
    attemptLocationDetection
  } = useUserLocation(user, currentUser);

  // Use the new data hook with user location - add logging
  const {
    jobs,
    loading,
    error,
    totalJobs,
    refreshing,
    searchTerm,
    locationFilter,
    employmentTypeFilter,
    sourceFilter,
    sortBy,
    roleFilter,
    distanceFilter,
    setSearchTerm,
    setLocationFilter,
    setEmploymentTypeFilter,
    setSourceFilter,
    setSortBy,
    setRoleFilter,
    setDistanceFilter,
    refreshJobs,
    clearFilters,
    cacheStats
  } = useJobData(userLocation);

  // Add basic debugging effect - ENHANCED with job filtering debug
  useEffect(() => {
    if (user && currentUser) {
      // REMOVED: Debug logging for production
      
      // CRITICAL DEBUG: Log role filter effectiveness with correct field
      if (roleFilter && roleFilter !== 'all' && jobs && jobs.length > 0) {
        // REMOVED: Debug logging for production
      }
    } else {
      // REMOVED: Debug logging for production
    }
  }, [user, currentUser, userLocation, jobs, loading, error, totalJobs, roleFilter, hasUserChangedRole]);

  // CRITICAL FIX: More robust role filter effect with better dependency management and enhanced debugging
  useEffect(() => {
    try {
      // REMOVED: Debug logging for production

      if (!hasUserChangedRole && 
          currentUser?.role && 
          roleFilter === 'all' && 
          !loading && 
          jobs && Array.isArray(jobs) && jobs.length > 0 && // Ensure jobs is valid array
          totalJobs > 0 && 
          currentUser.role) {
        
        // REMOVED: Debug logging for production
        
        // CRITICAL FIX: Add a small delay to ensure job filtering has completed
        const timeoutId = setTimeout(() => {
          // REMOVED: Debug logging for production
          
          const roleMapping = {
            'hygienist': 'Hygienist/Therapist',
            'therapist': 'Hygienist/Therapist', 
            'dental_hygienist': 'Hygienist/Therapist',
            'dental_therapist': 'Hygienist/Therapist',
            'locum_dentist': 'Locum Dentist',
            'dentist': 'Locum Dentist',
            'dental_nurse': 'Dental Nurse',
            'nurse': 'Dental Nurse'
          };
          
          const defaultRole = roleMapping[currentUser.role.toLowerCase()];
          if (defaultRole) {
            // REMOVED: Debug logging for production
            
            // CRITICAL FIX: Use functional update to ensure we have latest state
            setRoleFilter(prevFilter => {
              if (prevFilter === 'all') {
                // REMOVED: Debug logging for production
                return defaultRole;
              } else {
                // REMOVED: Debug logging for production
                return prevFilter;
              }
            });
            
            setHasUserChangedRole(true);
          } else {
            // REMOVED: Debug logging for production
          }
        }, 100); // Small delay to ensure jobs have finished loading
        
        return () => {
          // REMOVED: Debug logging for production
          clearTimeout(timeoutId);
        };
      } else {
        // REMOVED: Debug logging for production
      }
    } catch (error) {
      console.error('JobBrowsingPage: Error in role filter effect:', error);
    }
  }, [currentUser?.role, loading, hasUserChangedRole, roleFilter, setRoleFilter, jobs?.length, totalJobs]);

  // FIXED: Track when user manually changes role filter
  const handleRoleFilterChange = useCallback((newRole) => {
    try {
      setHasUserChangedRole(true); // Mark that user has manually changed the role
      setRoleFilter(newRole);
    } catch (error) {
      console.error('JobBrowsingPage: Error changing role filter:', error);
    }
  }, [setRoleFilter]);

  // Load user preferences - add error handling
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!user?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSavedJobs(userData.savedJobs || []);
        }
      } catch (error) {
        console.error('JobBrowsingPage: Error loading user preferences:', error);
        // Don't block the page for this error
      }
    };

    loadUserPreferences();
  }, [user?.uid]);

  // NEW: Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle job actions
  const handleSaveJob = async (jobId) => {
    if (!user?.uid) {
      console.warn('Please log in to save jobs.');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const isSaved = savedJobs.includes(jobId);

      if (isSaved) {
        await updateDoc(userRef, {
          savedJobs: arrayRemove(jobId)
        });
        setSavedJobs(prev => prev.filter(id => id !== jobId));
      } else {
        await updateDoc(userRef, {
          savedJobs: arrayUnion(jobId)
        });
        setSavedJobs(prev => [...prev, jobId]);
      }
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    refreshJobs();
  };

  // Listen for distance filter changes
  useEffect(() => {
    const handleDistanceFilterChange = (event) => {
      setCurrentDistanceFilter(event.detail.distance);
    };

    window.addEventListener('distanceFilterChanged', handleDistanceFilterChange);
    
    return () => {
      window.removeEventListener('distanceFilterChanged', handleDistanceFilterChange);
    };
  }, []);

  // FIXED: Enhanced clear filters that prevents automatic role setting
  const handleClearFilters = () => {
    // FIXED: Mark that user has manually changed role BEFORE clearing to prevent auto-setting
    setHasUserChangedRole(true); // This prevents the useEffect from setting profile role
    
    // Clear all filters (this will set roleFilter to 'all')
    clearFilters();
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Header - Mobile Optimized */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h1 className={cn(
                "font-bold text-xl sm:text-2xl md:text-3xl",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Find Jobs
              </h1>
              <HelpIcon sectionKey="JobBrowsingPage" />
            </div>
            <p className={cn(
              "text-sm sm:text-base md:text-lg leading-tight",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              <span className="sm:hidden">Find dental opportunities</span>
              <span className="hidden sm:inline">Discover dental job opportunities near you</span>
            </p>
          </div>
          
          {/* View Toggle and Refresh - Compact for mobile */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className={cn(
                "h-8 px-2 sm:h-9 sm:px-3", // Smaller on mobile
                theme === 'light' 
                  ? 'text-gray-700 border-gray-300 hover:bg-gray-100' 
                  : 'text-gray-300 border-gray-600 hover:bg-gray-700'
              )}
            >
              <RefreshCw className={cn("h-3 w-3 sm:h-4 sm:w-4", refreshing && "animate-spin")} />
              <span className="hidden sm:inline ml-2">Refresh</span>
            </Button>
            
            <div className={cn(
              "flex rounded-lg border",
              theme === 'light' ? 'border-gray-300' : 'border-gray-600'
            )}>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={cn(
                  "rounded-r-none h-8 px-2 sm:h-9 sm:px-3", // Smaller on mobile
                  viewMode === 'list' 
                    ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' 
                    : theme === 'light' 
                      ? 'text-gray-700 hover:bg-gray-100 border-gray-300 bg-white' 
                      : 'text-white hover:bg-gray-700 border-gray-600 bg-[#2A2A3E]'
                )}
              >
                <List className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline ml-2">List</span>
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={cn(
                  "rounded-l-none border-l-0 h-8 px-2 sm:h-9 sm:px-3", // Smaller on mobile
                  viewMode === 'map' 
                    ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' 
                    : theme === 'light' 
                      ? 'text-gray-700 hover:bg-gray-100 border-gray-300 bg-white' 
                      : 'text-white hover:bg-gray-700 border-gray-600 bg-[#2A2A3E]'
                )}
              >
                <MapIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline ml-2">Map</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Job Stats with Enhanced Location Display */}
        <div className={cn(
          "flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm",
          theme === 'light' ? 'text-gray-600' : 'text-gray-400'
        )}>
          <span>{typeof totalJobs === 'number' ? totalJobs : 0} jobs found</span>
          
          {/* ENHANCED: Better location display with update option */}
          <div className="flex items-center gap-1">
            <span className="hidden sm:inline">•</span>
            <LocationDisplay
              userLocation={userLocation}
              locationLoading={locationLoading}
              onUpdateLocation={() => setShowLocationPrompt(true)}
            />
          </div>
          
          {/* Add loading indicator */}
          {loading && <span className="text-purple-600">• Loading...</span>}
        </div>
      </div>

      {/* ENHANCED: Conditionally compact filters on mobile map view */}
      <div className={cn(
        viewMode === 'map' && isMobile ? "mb-2" : "mb-6" // Less margin on mobile map
      )}>
        <JobFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          employmentTypeFilter={employmentTypeFilter}
          setEmploymentTypeFilter={setEmploymentTypeFilter}
          sourceFilter={sourceFilter}
          setSourceFilter={setSourceFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          roleFilter={roleFilter}
          setRoleFilter={handleRoleFilterChange}
          distanceFilter={distanceFilter}
          setDistanceFilter={setDistanceFilter}
          onClearFilters={handleClearFilters}
          jobCount={totalJobs}
          filteredJobCount={jobs.length}
          // NEW: Pass mobile and map view state for compact mode
          isCompactMode={viewMode === 'map' && isMobile}
        />
      </div>

      {/* Error State */}
      {error && (
        <Card className={cn(
          "mb-6",
          theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-500/20'
        )}>
          <CardContent className="p-4">
            <p className={cn(
              "text-center",
              theme === 'light' ? 'text-red-700' : 'text-red-400'
            )}>
              {/* FIX: Ensure error is rendered as a string */}
              {typeof error === 'string' ? error : 'An error occurred'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Content */}
      {viewMode === 'map' ? (
        <>
          <CollapsibleMapLegend 
            theme={theme} 
            distanceFilter={currentDistanceFilter}
            isMobile={isMobile}
          />
          <JobMapView 
            jobs={jobs}
            onViewJob={handleViewJob}
            onSetViewMode={setViewMode}
            userLocation={userLocation}
          />
        </>
      ) : (
        <JobListView 
          jobs={jobs}
          loading={loading}
          onViewJob={handleViewJob}
          onClearFilters={clearFilters}
        />
      )}

      {/* Job Details Modal */}
      <JobDetailsModal
        job={selectedJob}
        isOpen={showJobDetails}
        onClose={() => setShowJobDetails(false)}
      />

      {/* Location prompt */}
      {showLocationPrompt && (
        <LocationPrompt
          onLocationSubmit={handleManualLocationEntry}
          onClose={() => setShowLocationPrompt(false)}
          loading={locationLoading}
        />
      )}
    </div>
  );
};

export default JobBrowsingPage;
